// Static /plans page (hash route "#plans") — three plan cards, no payments.
// PlansGate listens to the hash so the header Upgrade pill (and the
// "Unlock more with Pro Plan" link) can open it from anywhere.
import { useEffect, useState } from 'react';
import FullPageOverlay from './FullPageOverlay';
import { BRAND, PAYMENTS_VISIBLE } from '../brand';

const PLANS = [
  {
    name: 'Basic',
    price: '$19',
    period: '/month',
    tagline: 'For one piece of equipment',
    features: [
      '1 manual slot',
      '100 messages / month',
      'General Maritime Assistant',
      'Email support',
    ],
    highlighted: false,
  },
  {
    name: 'Pro',
    price: '$49',
    period: '/month',
    tagline: 'For the whole engine room',
    features: [
      '5 manual slots',
      '500 messages / month',
      'Priority technical model routing',
      'SIRE 2.0 inspection prep',
    ],
    highlighted: true,
  },
  {
    name: 'Chief',
    price: '$99',
    period: '/month',
    tagline: 'For chief engineers & managers',
    features: [
      '15 manual slots',
      '2000 messages / month',
      'Everything in Pro',
      'Early access to new modules',
    ],
    highlighted: false,
  },
];

export function PlansPage({ onBack }) {
  // Free beta: old #plans links keep working, but show a simple notice
  // instead of pricing until payments go live (PAYMENTS_VISIBLE flips it).
  if (!PAYMENTS_VISIBLE) {
    return (
      <FullPageOverlay label="Plans">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
          <button
            className="btn btn-ghost"
            onClick={onBack}
            style={{ padding: '9px 18px', fontSize: 12 }}
          >
            ← Back
          </button>
        </div>
        <div style={{ textAlign: 'center', padding: '48px 12px 40px' }}>
          <div style={{ fontSize: 40, marginBottom: 18 }} aria-hidden="true">
            ⛵
          </div>
          <h1
            style={{
              fontSize: 'clamp(24px, 4.5vw, 34px)',
              fontWeight: 800,
              margin: 0,
              color: 'var(--text)',
            }}
          >
            {BRAND.name} is currently free during our beta.
          </h1>
          <p
            style={{
              color: 'var(--text-2)',
              fontSize: 15,
              lineHeight: 1.7,
              maxWidth: 460,
              margin: '14px auto 0',
            }}
          >
            Paid plans will come later — early users will be the first to know.
          </p>
        </div>
      </FullPageOverlay>
    );
  }

  return (
    <FullPageOverlay label="Plans">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
        <button
          className="btn btn-ghost"
          onClick={onBack}
          style={{ padding: '9px 18px', fontSize: 12 }}
        >
          ← Back
        </button>
      </div>
      <div style={{ textAlign: 'center', margin: '10px 0 28px' }}>
        <h1
          style={{
            fontSize: 'clamp(26px, 4.5vw, 38px)',
            fontWeight: 800,
            margin: 0,
            color: 'var(--text)',
          }}
        >
          <span style={{ color: 'var(--text-3)' }}>Simple plans,</span> smooth sailing.
        </h1>
        <p style={{ color: 'var(--text-2)', fontSize: 14.5, marginTop: 10 }}>
          Every account starts with a 10-message trial on our top model, then stays free with 30
          messages/month — no card required. Approved community notes earn +20 messages each.
        </p>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 210px), 1fr))',
          gap: 16,
        }}
      >
        {PLANS.map(plan => (
          <div
            key={plan.name}
            style={{
              background: 'var(--surface-1, #ffffff)',
              border: plan.highlighted ? '2px solid var(--accent)' : '1px solid var(--border)',
              borderRadius: 22,
              boxShadow: 'var(--shadow-card)',
              padding: 24,
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
            }}
          >
            {plan.highlighted && (
              <span
                style={{
                  alignSelf: 'flex-start',
                  fontSize: 10.5,
                  fontWeight: 700,
                  letterSpacing: 1,
                  color: 'var(--accent)',
                  background: 'rgba(59,111,224,0.08)',
                  border: '1px solid rgba(59,111,224,0.25)',
                  borderRadius: 999,
                  padding: '3px 10px',
                  marginBottom: 4,
                }}
              >
                MOST POPULAR
              </span>
            )}
            <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--text)' }}>{plan.name}</div>
            <div style={{ color: 'var(--text)' }}>
              <span style={{ fontSize: 32, fontWeight: 800 }}>{plan.price}</span>
              <span style={{ color: 'var(--text-3)', fontSize: 13 }}>{plan.period}</span>
            </div>
            <div style={{ color: 'var(--text-2)', fontSize: 13, marginBottom: 10 }}>
              {plan.tagline}
            </div>
            <ul style={{ margin: 0, padding: 0, listStyle: 'none', flex: 1 }}>
              {plan.features.map(f => (
                <li
                  key={f}
                  style={{
                    fontSize: 13,
                    color: 'var(--text-2)',
                    padding: '5px 0',
                    display: 'flex',
                    gap: 8,
                  }}
                >
                  <span style={{ color: 'var(--green)' }}>✓</span> {f}
                </li>
              ))}
            </ul>
            <button
              className={plan.highlighted ? 'pill-primary' : 'btn btn-ghost'}
              style={{ marginTop: 14, justifyContent: 'center', width: '100%', minHeight: 44 }}
              onClick={onBack}
            >
              Coming soon
            </button>
          </div>
        ))}
      </div>

      <p style={{ textAlign: 'center', color: 'var(--text-3)', fontSize: 12, marginTop: 24 }}>
        Payments are not live yet — plans are managed manually during the pilot.
        <br />
        By subscribing you agree to our{' '}
        <a href="#terms" style={{ color: 'var(--text-3)' }}>
          Terms
        </a>
        .
      </p>
    </FullPageOverlay>
  );
}

// Renders PlansPage whenever the URL hash is #plans (set by the Upgrade pill).
export default function PlansGate() {
  const [hash, setHash] = useState(typeof window !== 'undefined' ? window.location.hash : '');

  useEffect(() => {
    const onHash = () => setHash(window.location.hash);
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  if (hash !== '#plans') return null;
  return (
    <PlansPage
      onBack={() => {
        window.location.hash = '';
      }}
    />
  );
}

export function goToPlans() {
  window.location.hash = '#plans';
}
